import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchTiers } from "../store/slices/subscriptionSlice";
import {
  RocketLaunchIcon,
  PaintBrushIcon,
  EnvelopeIcon,
  ChartBarIcon,
  ClockIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CheckIcon,
  StarIcon,
  ArrowRightIcon,
  Bars3Icon,
  XMarkIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

const FEATURES = [
  {
    icon: RocketLaunchIcon,
    title: "Build Quotes in Minutes",
    description:
      "Search thousands of cruise sailings, select cabin options, and create polished quotes in under 2 minutes. No more manual PDFs or spreadsheets.",
  },
  {
    icon: PaintBrushIcon,
    title: "Your Brand, Front & Center",
    description:
      "Custom colors, logo, agent photo, and tagline on every quote. Your clients see your brand, not ours.",
  },
  {
    icon: EnvelopeIcon,
    title: "One-Click Email Delivery",
    description:
      "Send beautiful, branded quote emails directly to clients. They get a professional link to view, compare, and accept options.",
  },
  {
    icon: ChartBarIcon,
    title: "Real-Time Tracking",
    description:
      "Know the moment a client opens your quote. Track views, time spent, and which cabin options they're considering.",
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "Mobile-Ready Client Portal",
    description:
      "Clients view and accept quotes from any device. The portal adapts beautifully to phones, tablets, and desktops.",
  },
  {
    icon: UserGroupIcon,
    title: "Built-In Client CRM",
    description:
      "Keep all your client contacts organized. See quote history, preferences, and conversion rates at a glance.",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Search & Select",
    description:
      "Browse real-time cruise inventory. Filter by cruise line, ship, region, or date. Pick the perfect sailing for your client.",
  },
  {
    number: "02",
    title: "Customize Your Quote",
    description:
      "Choose cabin categories, set pricing, add your personal message and notes. Mark your recommended option.",
  },
  {
    number: "03",
    title: "Send & Track",
    description:
      "Email the branded quote with one click. Get notified when your client views it. Follow up at exactly the right time.",
  },
  {
    number: "04",
    title: "Close the Booking",
    description:
      "Clients accept their preferred cabin right from the quote. You get notified instantly and finalize the booking.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "QuickQuote cut my quoting time from 45 minutes to under 5. My clients love the professional look and I've doubled my conversion rate.",
    name: "Sarah Mitchell",
    role: "Independent Travel Advisor",
    stars: 5,
  },
  {
    quote:
      "The real-time tracking is a game changer. I know exactly when to follow up, and the branded quotes make me look like a much bigger agency.",
    name: "David Chen",
    role: "Cruise Specialist, Wanderlust Travel",
    stars: 5,
  },
  {
    quote:
      "I was spending hours building PDF quotes. Now I search, click, and send. My clients can compare cabin options side by side. It's brilliant.",
    name: "Maria Rodriguez",
    role: "Owner, Paradise Cruise Planners",
    stars: 5,
  },
];

const FAQS = [
  {
    question: "Do I need my own cruise data or contracts?",
    answer:
      "No. QuickQuote connects to a comprehensive cruise database with thousands of sailings from major cruise lines. You search and quote directly from our inventory.",
  },
  {
    question: "Can my clients book directly through the quote?",
    answer:
      "Clients can accept their preferred cabin option through the quote, which notifies you immediately. You then finalize the booking through your normal booking channels. This keeps you in control of the booking process.",
  },
  {
    question: "How does branding work?",
    answer:
      "You upload your logo, set your brand colors, add your photo and tagline. Every quote your clients see is fully branded to your agency. Our name only appears in small print at the bottom.",
  },
  {
    question: "Is there a contract or commitment?",
    answer:
      "No contracts. All plans are month-to-month and you can cancel anytime. We also offer yearly billing with a 17% discount if you prefer to save.",
  },
  {
    question: "What happens when my client views a quote?",
    answer:
      "You get a real-time notification. The dashboard shows you exactly when the quote was viewed, how many times, and the current status. No more guessing if the client saw your email.",
  },
  {
    question: "Can I try it before paying?",
    answer:
      "Every new account starts with a 14-day free trial on the Professional plan. No credit card required. You get full access to all features so you can see the value before committing.",
  },
];

function Navbar() {
  const [open, setOpen] = useState(false);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <BoltIcon className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">
              Quick<span className="text-indigo-600">Quote</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 px-4 py-2 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-2.5 rounded-lg transition-colors"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setOpen(!open)} className="md:hidden p-2 text-gray-600">
            {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="md:hidden border-t border-gray-100 py-4 space-y-2">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="block px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2 px-3">
              <Link to="/login" className="text-sm font-medium text-gray-600 py-2">
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-2.5 rounded-lg text-center"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-indigo-100/50 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-purple-100/50 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <BoltIcon className="h-4 w-4" />
            Purpose-built for cruise travel agents
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight">
            Stop losing bookings to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
              slow quotes
            </span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Create beautiful, branded cruise quotes in minutes — not hours.
            Send them instantly, track client engagement in real time, and close
            more bookings.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-semibold px-8 py-4 rounded-xl text-base hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300"
            >
              Start Your Free Trial
              <ArrowRightIcon className="h-5 w-5" />
            </Link>
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-gray-700 font-semibold px-8 py-4 rounded-xl text-base border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
            >
              See How It Works
            </a>
          </div>

          <p className="mt-6 text-sm text-gray-500">
            14-day free trial &middot; No credit card required &middot; Cancel anytime
          </p>
        </div>

        {/* Dashboard preview mockup */}
        <div className="mt-16 relative">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Browser chrome */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-white border border-gray-200 rounded-md px-3 py-1 text-xs text-gray-400 max-w-md mx-auto">
                  quickquote.cruisecompare.ca/dashboard
                </div>
              </div>
            </div>

            {/* Mock dashboard content */}
            <div className="p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-white">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Total Quotes", value: "248", color: "indigo" },
                  { label: "Sent", value: "186", color: "blue" },
                  { label: "Viewed", value: "142", color: "amber" },
                  { label: "Accepted", value: "89", color: "green" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
                    <p className={`text-2xl font-bold text-${stat.color}-600 mt-1`}>{stat.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { line: "Royal Caribbean", ship: "Wonder of the Seas", date: "Mar 15, 2026", status: "Viewed", statusColor: "amber" },
                  { line: "Norwegian", ship: "Norwegian Prima", date: "Apr 8, 2026", status: "Sent", statusColor: "blue" },
                  { line: "Celebrity", ship: "Celebrity Edge", date: "May 22, 2026", status: "Accepted", statusColor: "green" },
                ].map((quote) => (
                  <div key={quote.ship} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-400 font-medium">{quote.line}</p>
                    <p className="font-semibold text-gray-900 text-sm mt-0.5">{quote.ship}</p>
                    <p className="text-xs text-gray-500 mt-1">{quote.date}</p>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full bg-${quote.statusColor}-50 text-${quote.statusColor}-700`}>
                      {quote.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-200/20 via-purple-200/20 to-indigo-200/20 rounded-3xl blur-2xl -z-10" />
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">
            Everything You Need
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Quoting tools designed for cruise specialists
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Everything you need to create, send, and manage cruise quotes — all in one place.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="group relative bg-gray-50 hover:bg-white rounded-2xl p-8 transition-all hover:shadow-lg border border-transparent hover:border-gray-200"
            >
              <div className="h-12 w-12 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-5 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <feature.icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">
            Simple Process
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            From search to booking in 4 steps
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            No complicated setup. No learning curve. Just faster quoting.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((step, idx) => (
            <div key={step.number} className="relative">
              {idx < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-indigo-200 to-transparent -translate-x-4" />
              )}
              <div className="text-5xl font-extrabold text-indigo-100 mb-4">{step.number}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="py-16 bg-indigo-600">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            { value: "2 min", label: "Average time to create a quote" },
            { value: "3x", label: "Faster than manual PDF quotes" },
            { value: "47%", label: "Higher client response rate" },
            { value: "10K+", label: "Cruise sailings available" },
          ].map((stat) => (
            <div key={stat.label}>
              <p className="text-3xl sm:text-4xl font-extrabold text-white">{stat.value}</p>
              <p className="mt-2 text-sm text-indigo-200">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">
            Trusted by Agents
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Agents love QuickQuote
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name}
              className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <StarSolid key={i} className="h-5 w-5 text-amber-400" />
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">"{t.quote}"</p>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const dispatch = useDispatch();
  const { tiers } = useSelector((state) => state.subscription);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    dispatch(fetchTiers());
  }, [dispatch]);

  return (
    <section id="pricing" className="py-20 sm:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">
            Pricing
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Start with a 14-day free trial. No credit card required.
          </p>

          <div className="mt-8 inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 text-sm font-medium rounded-md transition-all ${
                billing === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-5 py-2 text-sm font-medium rounded-md transition-all ${
                billing === "yearly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Yearly{" "}
              <span className="text-green-600 text-xs font-semibold ml-1">Save 17%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white rounded-2xl border-2 p-8 transition-shadow hover:shadow-lg ${
                tier.popular ? "border-indigo-600 shadow-xl" : "border-gray-200"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
              <div className="mt-4 mb-2">
                <span className="text-4xl font-bold text-gray-900">
                  ${billing === "yearly" ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice}
                </span>
                <span className="text-gray-500 ml-1">/month</span>
              </div>
              {billing === "yearly" && (
                <p className="text-sm text-green-600 mb-4">
                  ${tier.yearlyPrice}/year (save ${tier.monthlyPrice * 12 - tier.yearlyPrice})
                </p>
              )}
              {billing === "monthly" && <p className="text-sm text-gray-500 mb-4">&nbsp;</p>}

              <p className="text-sm text-gray-600 mb-6">
                {tier.quotesPerMonth === -1
                  ? "Unlimited quotes per month"
                  : `Up to ${tier.quotesPerMonth} quotes per month`}
              </p>

              <Link
                to="/register"
                className={`block w-full py-3 rounded-xl font-semibold text-sm text-center transition-colors ${
                  tier.popular
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                }`}
              >
                Start Free Trial
              </Link>

              <ul className="mt-8 space-y-3">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="py-20 sm:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">
            FAQ
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Common questions
          </h2>
        </div>

        <div className="space-y-3">
          {FAQS.map((faq, idx) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-xl overflow-hidden transition-colors hover:border-gray-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
              >
                <span className="font-semibold text-gray-900 text-sm sm:text-base">
                  {faq.question}
                </span>
                <span
                  className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === idx ? "rotate-45" : ""
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
              </button>
              {openIndex === idx && (
                <div className="px-6 pb-5 -mt-1">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 sm:py-28 bg-gradient-to-br from-indigo-600 to-purple-700 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA4KSIvPjwvc3ZnPg==')] opacity-60" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Ready to quote cruises faster?
        </h2>
        <p className="text-lg text-indigo-200 mb-10 max-w-xl mx-auto">
          Join travel agents who are saving hours every week and closing more
          bookings with QuickQuote.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-white text-indigo-700 font-semibold px-8 py-4 rounded-xl text-base hover:bg-indigo-50 transition-colors shadow-lg"
        >
          Start Your 14-Day Free Trial
          <ArrowRightIcon className="h-5 w-5" />
        </Link>
        <p className="mt-5 text-sm text-indigo-300">
          No credit card required &middot; Full access to all features
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-900 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center">
              <BoltIcon className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">
              Quick<span className="text-indigo-400">Quote</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
            <a href="#features" className="hover:text-white transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-white transition-colors">
              Pricing
            </a>
            <a href="#faq" className="hover:text-white transition-colors">
              FAQ
            </a>
            <Link to="/login" className="hover:text-white transition-colors">
              Sign In
            </Link>
          </div>

          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} QuickQuote by CruiseCompare
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <Stats />
      <Testimonials />
      <PricingSection />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
